import React from "react";
import "./AddWigRow.scss";

// ═════════════════════════════════════════════════════════════════════════════
// The three fields a new WIG is made of. ⚠️ IDENTICAL IN TWO REPOSITORIES.
//
// Extracted from `AddWigRow` when the connections map asked for a DIALOG rather
// than an inline row (2026-09-14). The two presentations are genuinely
// different — a row that opens in a list, and a modal over a canvas — but the
// fields inside them must not be: same labels, same placeholders, same
// required/optional marks, same limits.
//
// Left as a dumb controlled component. It owns no state and does no
// validation, so there is exactly one copy of the shape and one copy of the
// wording, while each host keeps its own lifecycle — the row closes itself on
// success, the dialog is unmounted by its screen.
//
// ── What the contract requires ──────────────────────────────────────────────
// Handoff #25 §2 types `add_live_review_item` as
// `{ section_key, label, value, rationale? }`. Only `rationale` carries the
// `?`, so Name and The goal are both required. `canSubmitWig` below is that
// rule, exported so a host cannot decide it differently.
// ═════════════════════════════════════════════════════════════════════════════

export const WIG_LABEL_MAX = 80;
export const WIG_TEXT_MAX = 400;

/** The contract's own requirement, in one place. */
export const canSubmitWig = (draft) =>
  !!(draft?.label || "").trim() && !!(draft?.value || "").trim();

/**
 * "Financial WIGs" → "a Financial WIG", not "a Financial WIGs WIG".
 *
 * ⚠️ THE SECTION LABELS ALREADY CARRY THE WORD. Every caller passes a section
 * label — "Financial WIGs", or a server-sent one that may still be singular —
 * so appending blindly doubled it, and the add row read "Add a Financial WIG
 * WIG".
 *
 * This phrase names exactly ONE goal, so it cannot simply pass a plural label
 * through. Either number reduces to the same phrase, and a label carrying no
 * suffix at all still gets one, so it keeps reading correctly if the titles are
 * reworded again.
 */
export const wigPhraseFor = (sectionLabel) => {
  const label = (sectionLabel || "").trim();
  if (!label) return "a WIG";
  // "an Operations WIG", not "a Operations WIG". A vowel test rather than a
  // word list because the four dimensions are not the only labels this will
  // ever see — the server owns them, and one of today's four already needs it.
  //
  // Reduce to the bare dimension first, then append the singular — the only
  // form that survives a label in either number. `s?` is what covers both.
  const base = label.replace(/\s*WIGs?$/i, "").trim() || label;
  const article = /^[aeiou]/i.test(base) ? "an" : "a";
  return `${article} ${base} WIG`;
};

/** Trims a draft into the shape the wire wants. */
export const toWigPayload = (draft) => ({
  label: (draft?.label || "").trim(),
  value: (draft?.value || "").trim(),
  rationale: (draft?.rationale || "").trim(),
});

/**
 * @param idPrefix  Makes the `htmlFor`/`id` pairs unique — several of these can
 *                  be on one screen at once (one per section on stage 1).
 * @param draft     `{ label, value, rationale }`.
 * @param onChange  `(patch) => void`, called with the one changed field.
 * @param firstFieldRef  Optional, so the host can focus the first input.
 */
const WigFieldset = ({ idPrefix, draft, onChange, firstFieldRef }) => (
  <>
    <div className="awr-field">
      <label className="awr-label" htmlFor={`${idPrefix}-name`}>
        Name
      </label>
      <input
        id={`${idPrefix}-name`}
        ref={firstFieldRef}
        className="awr-input"
        type="text"
        value={draft.label}
        maxLength={WIG_LABEL_MAX}
        placeholder="e.g. Client flow"
        onChange={(event) => onChange({ label: event.target.value })}
      />
    </div>

    <div className="awr-field">
      <label className="awr-label" htmlFor={`${idPrefix}-value`}>
        The goal
      </label>
      {/* The one field the card cannot render without — a WIG with no wording
          is an empty row on the map. */}
      <textarea
        id={`${idPrefix}-value`}
        className="awr-input awr-input--area"
        rows={2}
        value={draft.value}
        maxLength={WIG_TEXT_MAX}
        placeholder="What has to be true before your BHAG deadline?"
        onChange={(event) => onChange({ value: event.target.value })}
      />
    </div>

    <div className="awr-field">
      <label className="awr-label" htmlFor={`${idPrefix}-why`}>
        Why it matters <span className="awr-optional">optional</span>
      </label>
      <textarea
        id={`${idPrefix}-why`}
        className="awr-input awr-input--area"
        rows={2}
        value={draft.rationale}
        maxLength={WIG_TEXT_MAX}
        placeholder="What this unlocks, or what breaks without it"
        onChange={(event) => onChange({ rationale: event.target.value })}
      />
    </div>
  </>
);

export default WigFieldset;
